import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AssistantCard, AssistantMessage, AssistantPostProposalCard, AssistantReplyProposalCard, AssistantScheduleProposalCard } from '@unipostudio/shared-types';
import { postsService } from '@/shared/services/posts.service';
import { inboxService } from '@/shared/services/inbox.service';
import { PLATFORM_RULES } from '@/shared/lib/platform-rules';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import { Sheet } from '@/shared/components/Sheet';
import { PlatformIcon, type PlatformId } from '@/shared/components/PlatformIcon';
import { assistantService } from '@/shared/services/assistant.service';
import { CALENDAR_SLOTS_KEY, calendarSlotsService } from '@/shared/services/calendar-slots.service';
import { useAskStore, type AskTurn } from '@/shared/stores/ask.store';
import { getApiErrorMessage } from '@/shared/lib/api-errors';
import { formatDate, formatDateTime, formatRelative, formatTime } from '@/shared/lib/intl';

/** Router state the panel hands the composer for a suggested post (see ComposerPage). */
export interface ComposerAssistantDraftState {
  assistantDraft: Pick<AssistantPostProposalCard, 'content' | 'platforms' | 'scheduledAt'>;
}

const SUGGESTIONS = ['ask.suggest.waiting', 'ask.suggest.lastWeek', 'ask.suggest.findChat', 'ask.suggest.writePost', 'ask.suggest.schedule'] as const;

function Platforms({ list }: { list: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {list.map((p) => (
        <PlatformIcon key={p} platform={p.toLowerCase() as PlatformId} size={14} decorative />
      ))}
    </span>
  );
}

/** A brand day (YYYY-MM-DD) and HH:mm, worded in the locale without shifting through the viewer's zone. */
const slotDay = (date: string): string => formatDate(`${date}T12:00:00Z`, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const slotTime = (time: string): string => formatTime(`1970-01-01T${time}:00`);

/**
 * A suggested posting schedule (UI v2 AI scheduler). Adding it makes the
 * calendar slots one by one through the ordinary slots API, as the person's
 * own; a slot that fails stays to be added again, the others are not repeated.
 */
function ScheduleCard({ card, onGo }: { card: AssistantScheduleProposalCard; onGo: (href: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [added, setAdded] = useState<ReadonlySet<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const total = card.slots.length;
  const done = added.size === total;

  const add = async (): Promise<void> => {
    setBusy(true);
    setFailed(false);
    const next = new Set(added);
    for (const [i, slot] of card.slots.entries()) {
      if (next.has(i)) continue;
      try {
        await calendarSlotsService.create({ date: slot.date, time: slot.time, label: slot.label, platforms: slot.platforms, notes: slot.notes, campaign: card.campaign });
        next.add(i);
      } catch {
        setFailed(true);
      }
    }
    setAdded(next);
    setBusy(false);
    void qc.invalidateQueries({ queryKey: [CALENDAR_SLOTS_KEY] });
  };

  return (
    <div className="p-3 rounded-[12px] border border-[var(--color-ai-line)] bg-[var(--color-ai-soft)]" data-testid="ask-card-schedule">
      <div className="flex items-center gap-1.5 text-caption font-semibold text-[var(--color-ai-text)] mb-2">
        <Icons.calendar size={12} aria-hidden="true" />
        {t('ask.card.suggestedSchedule')}
        {card.campaign && <span className="font-normal text-[var(--ink-2)] truncate" dir="auto">· {card.campaign}</span>}
      </div>
      <ul className="m-0 p-0 list-none flex flex-col divide-y divide-[var(--line)] rounded-[10px] bg-[var(--color-surface)] border border-[var(--line)] max-h-72 overflow-y-auto">
        {card.slots.map((slot, i) => (
          <li key={`${slot.date}-${slot.time ?? ''}-${i}`} className="flex items-center gap-2.5 px-3 py-2">
            <span className="w-24 shrink-0 text-caption text-[var(--ink-2)] tabular-nums">
              <span className="block font-semibold text-[var(--ink)]">{slotDay(slot.date)}</span>
              {slot.time ? slotTime(slot.time) : t('ask.card.anyTime')}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-ui truncate" dir="auto">{slot.label}</span>
              {slot.notes && <span className="block text-caption text-[var(--ink-3)] truncate" dir="auto">{slot.notes}</span>}
            </span>
            <Platforms list={slot.platforms} />
            {added.has(i) && <Icons.check size={14} className="text-[var(--good)] shrink-0" aria-label={t('ask.card.addedOne')} />}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2 mt-2.5" aria-live="polite">
        {done ? (
          <>
            <span className="text-caption font-semibold text-[var(--good)]">{t('ask.card.addedAll', { n: total })}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGo(ROUTES.CALENDAR)}>
              {t('ask.card.openCalendar')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void add()}>
              {busy ? t('ask.card.adding') : added.size > 0 ? t('ask.card.addRest') : t('ask.card.addToCalendar', { n: total })}
            </button>
            <span className={`text-caption ${failed ? 'text-[var(--bad)]' : 'text-[var(--ink-3)]'}`}>
              {failed ? t('ask.card.addedSome', { done: added.size, n: total }) : t('ask.card.nothingAdded')}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** The first (default) post type of a channel, as Compose would pick it. */
const defaultType = (platform: string): string => PLATFORM_RULES.find((r) => r.name === platform)?.types[0]?.id ?? 'Post';

/**
 * A post the assistant wrote. Saving a draft changes nothing anyone sees, so
 * it happens on one tap; scheduling is the person's decision too, shown with
 * the time it will go out. Anything the channel refuses (a post that needs a
 * photo) says why and leaves Compose as the way on.
 */
function PostProposalCard({ card, onGo }: { card: AssistantPostProposalCard; onGo: (href: string, state?: unknown) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [done, setDone] = useState<'draft' | 'scheduled' | null>(null);
  const [postId, setPostId] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (schedule: boolean) =>
      postsService.create({
        caption: card.content,
        platforms: card.platforms.map((p) => ({ platform: p, postType: defaultType(p) })),
        mediaIds: [],
        scheduledAt: schedule ? card.scheduledAt : null,
        publishNow: false,
      }),
    onSuccess: (post, schedule) => {
      setDone(schedule ? 'scheduled' : 'draft');
      setPostId(post.id);
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
  });
  return (
    <div className="p-3 rounded-[12px] border border-[var(--color-ai-line)] bg-[var(--color-ai-soft)]" data-testid="ask-card-proposal">
      <div className="flex items-center gap-1.5 text-caption font-semibold text-[var(--color-ai-text)] mb-1.5">
        <Icons.sparkles size={12} aria-hidden="true" />
        {t('ask.card.suggestedPost')}
      </div>
      <p className="m-0 text-ui whitespace-pre-wrap" dir="auto">{card.content}</p>
      <div className="flex items-center gap-2 mt-2 text-caption text-[var(--ink-3)]">
        <Platforms list={card.platforms} />
        {card.scheduledAt && formatDateTime(card.scheduledAt)}
      </div>
      {done ? (
        <div className="flex items-center gap-2 mt-2.5 flex-wrap" role="status">
          <span className="inline-flex items-center gap-1 text-caption font-semibold text-[var(--good)]">
            <Icons.check size={14} aria-hidden="true" />
            {done === 'scheduled' ? t('ask.card.scheduled') : t('ask.card.draftSaved')}
          </span>
          {postId && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGo(ROUTES.COMPOSER_EDIT.replace(":id", encodeURIComponent(postId)))}>
              {t('ask.card.openPost')}
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          {card.scheduledAt && card.platforms.length > 0 && (
            <button type="button" className="btn btn-primary btn-sm" disabled={save.isPending} onClick={() => save.mutate(true)}>
              {t('ask.card.scheduleAt', { when: formatDateTime(card.scheduledAt) })}
            </button>
          )}
          <button type="button" className={`btn btn-sm ${card.scheduledAt ? 'btn-secondary' : 'btn-primary'}`} disabled={save.isPending || card.platforms.length === 0} onClick={() => save.mutate(false)}>
            {t('ask.card.saveDraft')}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onGo(ROUTES.COMPOSER, { assistantDraft: { content: card.content, platforms: card.platforms, scheduledAt: card.scheduledAt } } satisfies ComposerAssistantDraftState)}
          >
            {t('ask.card.openInCompose')}
          </button>
          {save.isError && <span className="text-caption text-[var(--bad)] basis-full" role="alert">{getApiErrorMessage(save.error)}</span>}
        </div>
      )}
    </div>
  );
}

/** A reply the assistant wrote: editable, and sent to the customer only on Send. */
function ReplyProposalCard({ card, onGo }: { card: AssistantReplyProposalCard; onGo: (href: string) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [text, setText] = useState(card.reply);
  const send = useMutation({
    mutationFn: () => inboxService.reply({ eventId: card.eventId, message: text.trim() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
  return (
    <div className="p-3 rounded-[12px] border border-[var(--color-ai-line)] bg-[var(--color-ai-soft)]" data-testid="ask-card-reply">
      <div className="flex items-center gap-2 mb-1.5">
        <PlatformIcon platform={card.channel.toLowerCase() as PlatformId} size={16} decorative />
        <span className="text-caption font-semibold text-[var(--color-ai-text)] flex-1 truncate" dir="auto">{t('ask.card.replyTo', { name: card.to })}</span>
      </div>
      <p className="m-0 mb-2 text-caption text-[var(--ink-2)] line-clamp-2" dir="auto">“{card.customerText}”</p>
      {send.isSuccess ? (
        <div className="flex items-center gap-2 flex-wrap" role="status">
          <span className="inline-flex items-center gap-1 text-caption font-semibold text-[var(--good)]">
            <Icons.check size={14} aria-hidden="true" />
            {t('ask.card.replySent')}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onGo(card.href)}>{t('ask.card.openAtMessage')}</button>
        </div>
      ) : (
        <>
          <textarea className="textarea w-full min-h-[72px] text-ui" value={text} onChange={(e) => setText(e.target.value)} dir="auto" aria-label={t('ask.card.replyTo', { name: card.to })} />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button type="button" className="btn btn-primary btn-sm" disabled={send.isPending || !text.trim()} onClick={() => send.mutate()}>
              <Icons.send size={14} aria-hidden="true" />
              {send.isPending ? t('ask.card.sending') : t('ask.card.send')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGo(card.href)}>{t('ask.card.openAtMessage')}</button>
            {send.isError && <span className="text-caption text-[var(--bad)] basis-full" role="alert">{getApiErrorMessage(send.error)}</span>}
          </div>
        </>
      )}
    </div>
  );
}

function Card({ card, onGo }: { card: AssistantCard; onGo: (href: string, state?: unknown) => void }) {
  const { t } = useTranslation();
  const row = 'w-full text-start flex gap-2.5 items-start p-3 rounded-[12px] border border-[var(--line)] bg-[var(--color-surface)] hover:border-[var(--line-2)] cursor-pointer';
  switch (card.type) {
    case 'conversation':
      return (
        <button type="button" className={row} onClick={() => onGo(card.href)} data-testid="ask-card-conversation">
          <PlatformIcon platform={card.channel.toLowerCase() as PlatformId} size={18} decorative />
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2">
              <span className="text-ui font-semibold truncate flex-1" dir="auto">{card.from}</span>
              <span className="text-caption text-[var(--ink-3)] shrink-0">{formatRelative(card.at)}</span>
            </span>
            <span className="block text-caption text-[var(--ink-2)] line-clamp-2" dir="auto">{card.text}</span>
            <span className="inline-flex items-center gap-1 mt-1 text-caption font-semibold text-[var(--color-accent-text)]">
              <Icons.cornerDownRight size={12} aria-hidden="true" />
              {t('ask.card.openAtMessage')}
            </span>
          </span>
        </button>
      );
    case 'post':
      return (
        <button type="button" className={row} onClick={() => onGo(card.href)}>
          <Icons.posts size={18} className="text-[var(--ink-3)] shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0">
            <span className="block text-ui line-clamp-2" dir="auto">{card.content}</span>
            <span className="flex items-center gap-2 mt-1 text-caption text-[var(--ink-3)]">
              <Platforms list={card.platforms} />
              {t(`posts.status.${card.status}`, { defaultValue: card.status })}
              {card.when && ` · ${formatDateTime(card.when)}`}
            </span>
          </span>
        </button>
      );
    case 'post_proposal':
      return <PostProposalCard card={card} onGo={onGo} />;
    case 'reply_proposal':
      return <ReplyProposalCard card={card} onGo={onGo} />;
    case 'schedule_proposal':
      return <ScheduleCard card={card} onGo={onGo} />;
    case 'link':
      return (
        <button type="button" className="btn btn-secondary btn-sm self-start" onClick={() => onGo(card.href)}>
          {card.label}
          <Icons.arrowRight size={14} className="rtl:flip" aria-hidden="true" />
        </button>
      );
    default:
      return null;
  }
}

/**
 * The Ask UniPostudio conversation: its turns, sending, Try again, and the
 * question handed over by the palette or the Home box (sent once `active`).
 * Shared by the side panel and the full /ask page.
 */
function useAskConversation(active: boolean) {
  const { t } = useTranslation();
  const { turns, add, dropLast, clear, takePending } = useAskStore();
  const ask = useMutation({ mutationFn: (messages: AssistantMessage[]) => assistantService.ask(messages) });

  const send = (question: string): void => {
    const q = question.trim();
    if (!q || ask.isPending) return;
    const history = useAskStore.getState().turns.filter((x) => !x.failed);
    const userTurn: AskTurn = { id: crypto.randomUUID(), role: 'user', content: q, cards: [] };
    add(userTurn);
    const messages: AssistantMessage[] = [...history, userTurn].map((x) => ({ role: x.role, content: x.content }));
    ask.mutate(messages, {
      onSuccess: (res) => add({ id: crypto.randomUUID(), role: 'assistant', content: res.reply, cards: res.cards }),
      onError: (err) => add({ id: crypto.randomUUID(), role: 'assistant', content: getApiErrorMessage(err, t('ask.failed')), cards: [], failed: true }),
    });
  };

  useEffect(() => {
    if (!active) return;
    const q = takePending();
    if (q) send(q);
    // send reads the store directly; only becoming active with a pending question matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const retry = (): void => {
    const lastUser = [...turns].reverse().find((x) => x.role === 'user');
    dropLast();
    if (lastUser) {
      useAskStore.setState((s) => ({ turns: s.turns.filter((x) => x.id !== lastUser.id) }));
      send(lastUser.content);
    }
  };

  return { turns, send, retry, clear, pending: ask.isPending };
}

function AskThread({ conv, onGo }: { conv: ReturnType<typeof useAskConversation>; onGo: (href: string, state?: unknown) => void }) {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [conv.turns.length, conv.pending]);
  return (
    <div className="flex flex-col gap-4" aria-live="polite">
      {conv.turns.length === 0 && (
        <div className="flex flex-col items-center text-center gap-2 py-6">
          <span className="w-14 h-14 rounded-full flex items-center justify-center text-2xl bg-[var(--color-ai-soft)]" aria-hidden="true">✨</span>
          <h3 className="m-0 text-title font-semibold">{t('ask.emptyTitle')}</h3>
          <p className="m-0 text-ui text-[var(--ink-3)] max-w-[360px]">{t('ask.emptyBody')}</p>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {SUGGESTIONS.map((k) => (
              <button key={k} type="button" className="chip" onClick={() => conv.send(t(k))}>
                {t(k)}
              </button>
            ))}
          </div>
        </div>
      )}
      {conv.turns.map((turn) =>
        turn.role === 'user' ? (
          <div key={turn.id} className="self-end max-w-[85%] px-3.5 py-2.5 rounded-[14px] rounded-ee-[4px] bg-[var(--color-accent-soft)] border border-[var(--color-accent-border)] text-ui whitespace-pre-wrap" dir="auto">
            {turn.content}
          </div>
        ) : (
          <div key={turn.id} className="flex gap-2.5">
            <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-[var(--color-ai-soft)] text-[var(--color-ai-text)]" aria-hidden="true">
              <Icons.sparkles size={14} />
            </span>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <p className={`m-0 text-ui whitespace-pre-wrap ${turn.failed ? 'text-[var(--bad)]' : ''}`} dir="auto"><AnswerText text={turn.content} /></p>
              {turn.failed && (
                <button type="button" className="btn btn-secondary btn-sm self-start" onClick={conv.retry}>
                  {t('ask.retry')}
                </button>
              )}
              {turn.cards.map((card, i) => (
                <Card key={`${turn.id}-${i}`} card={card} onGo={onGo} />
              ))}
            </div>
          </div>
        ),
      )}
      {conv.pending && (
        <div className="flex gap-2.5 items-center text-ui text-[var(--ink-3)]" role="status">
          <span className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--color-ai-soft)] text-[var(--color-ai-text)]" aria-hidden="true">
            <Icons.sparkles size={14} />
          </span>
          {t('ask.thinking')}
        </div>
      )}
      {conv.turns.length > 0 && !conv.pending && (
        <button type="button" className="btn btn-ghost btn-sm self-center" onClick={conv.clear}>
          {t('ask.newChat')}
        </button>
      )}
      <div ref={endRef} />
    </div>
  );
}

function AskInput({ conv }: { conv: ReturnType<typeof useAskConversation> }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const submit = (): void => {
    if (!text.trim()) return;
    conv.send(text);
    setText('');
  };
  return (
    <form
      className="flex items-end gap-2 w-full"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        rows={2}
        maxLength={4000}
        placeholder={t('ask.placeholder')}
        aria-label={t('ask.placeholder')}
        className="input flex-1 min-h-[48px] max-h-36 resize-none py-2"
        dir="auto"
      />
      <button type="submit" className="btn btn-primary w-11 h-11 p-0 shrink-0" disabled={!text.trim() || conv.pending} aria-label={t('ask.send')}>
        <Icons.send size={16} aria-hidden="true" />
      </button>
    </form>
  );
}

/**
 * Ask UniPostudio (UI v2): a side panel from the top bar's "Ask or search"
 * (and the palette's Ask row, and the Home box). Answers come from the
 * brand's own data; found messages and posts are cards that open the exact
 * place; a suggested post opens in Compose, where the person schedules it.
 * Nothing is changed by asking. Not mounted on the full /ask page.
 */
export function AskPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, close } = useAskStore();
  const conv = useAskConversation(open);
  const go = (href: string, state?: unknown): void => {
    close();
    void navigate(href, state ? { state } : undefined);
  };
  return (
    <Sheet
      open={open}
      onClose={close}
      title={t('ask.title')}
      id="ask-panel"
      width={480}
      footer={
        <div className="flex flex-col gap-1.5 w-full">
          <AskInput conv={conv} />
          <button type="button" className="btn btn-ghost btn-sm self-end" onClick={() => go(ROUTES.ASK)}>
            {t('ask.openFull')}
          </button>
        </div>
      }
    >
      <AskThread conv={conv} onGo={go} />
    </Sheet>
  );
}

/** The full-page Ask UniPostudio (/ask), for longer sessions: the same conversation as the panel. */
export function AskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const conv = useAskConversation(true);
  const go = (href: string, state?: unknown): void => {
    void navigate(href, state ? { state } : undefined);
  };
  return (
    <div className="max-w-[820px] mx-auto">
      <h1 className="font-display text-page m-0 mb-1">{t('ask.title')}</h1>
      <p className="m-0 mb-5 text-ui text-[var(--ink-3)]">{t('ask.pageDescription')}</p>
      <section className="card p-4 sm:p-5 flex flex-col gap-4">
        <div className="min-h-[320px]">
          <AskThread conv={conv} onGo={go} />
        </div>
        <div className="sticky bottom-[calc(80px+env(safe-area-inset-bottom,0px))] lg:bottom-4 bg-[var(--color-surface)] pt-2">
          <AskInput conv={conv} />
        </div>
      </section>
    </div>
  );
}

/**
 * The assistant writes a little Markdown: **bold** names and "- " lists.
 * Shown as bold text and bullets, never as raw asterisks; nothing else is
 * interpreted, so a message quoted in an answer cannot inject markup.
 */
export function AnswerText({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, i, all) => {
        const bullet = /^\s*[-*•]\s+/.test(line);
        const body = bullet ? line.replace(/^\s*[-*•]\s+/, '') : line;
        const parts = body.split(/(\*\*[^*\n]+\*\*)/g).filter((p) => p !== '');
        return (
          <span key={i}>
            {bullet && '• '}
            {parts.map((part, j) =>
              /^\*\*[^*\n]+\*\*$/.test(part) ? <strong key={j}>{part.slice(2, -2)}</strong> : <span key={j}>{part}</span>,
            )}
            {i < all.length - 1 && '\n'}
          </span>
        );
      })}
    </>
  );
}
